import { PoolImage } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { Stack, styled } from '@mui/material';
import { shouldForwardPropExclude } from '@synergy/utils';

import { ArticlePlaceholderImage } from '../../../components/article-placeholder-image/article-placeholder-image';
import { SquareBox } from '../../../components/square-box/square-box';
import { useArticleDetailContext } from '../article-detail';

const StyledImagePreviewBox = styled(SquareBox, { shouldForwardProp: shouldForwardPropExclude('isSelected') })<{
    isSelected: boolean;
}>(({ theme, isSelected }) => ({
    border: `1.5px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px',
    cursor: 'pointer',
    width: '100%',
    maxHeight: theme.spacing(11),
    maxWidth: theme.spacing(11),
    minWidth: theme.spacing(5),

    '&:hover': {
        borderColor: theme.palette.common.black,
    },

    ...(isSelected && {
        borderColor: theme.palette.primary.main,
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    }),
}));

const PreviewColumn = styled(Stack)(({ theme }) => ({
    width: '20%',
    maxWidth: 104,
    paddingRight: theme.spacing(2),
    overflowY: 'auto',
    overflowX: 'hidden',
}));

export const ArticleDetailPreviewImageDesktop = (): ReactElement | null => {
    const article = useArticleDetailContext().article;
    const images = article.images;
    const [selectedPreviewImage, setSelectedPreviewImage] = useState(images[0]);
    const [hoverPreviewImage, setHoverPreviewImage] = useState<string>();

    useEffect(() => {
        setSelectedPreviewImage(images[0]);
    }, [images]);

    const selectedImage = images.find(image =>
        hoverPreviewImage ? image === hoverPreviewImage : image === selectedPreviewImage
    );

    if (images.length === 0) {
        return <ArticlePlaceholderImage supplier={article.supplier} imageCachePath="620x620" />;
    }

    return (
        <Stack direction="row" spacing={'3%'} height={616} maxWidth={740}>
            <PreviewColumn spacing={'20%'}>
                {images.map(image => (
                    <StyledImagePreviewBox
                        key={image}
                        isSelected={image === selectedPreviewImage}
                        onClick={() => setSelectedPreviewImage(image)}
                        onMouseEnter={() => setHoverPreviewImage(image)}
                        onMouseLeave={() => setHoverPreviewImage(undefined)}
                    >
                        <PoolImage
                            src={image}
                            alt={article.name}
                            style={{
                                objectFit: 'contain',
                                width: '100%',
                                height: '100%',
                                padding: '10%',
                            }}
                        />
                    </StyledImagePreviewBox>
                ))}
            </PreviewColumn>
            <Stack
                justifyContent="center"
                alignItems="center"
                sx={{ width: '100%', height: '100%', maxWidth: 616, maxHeight: 616 }}
            >
                {selectedImage && (
                    <PoolImage
                        src={selectedImage}
                        alt={article.name}
                        style={{
                            objectFit: 'contain',
                            maxWidth: '100%',
                            maxHeight: '100%',
                        }}
                    />
                )}
            </Stack>
        </Stack>
    );
};
